import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseRespone } from "src/app/models/common.response";
import { environment } from "src/environments/environment";
import { BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap } from "rxjs";
import { AddDiagnosisFromMasterRequest, BulkAddDiagnosisFromMasterRequest, BulkDiagnosisOutcomeResult, DiagnosisFromMasterResult, Iopd, IpatientToOPD, OPDRecord } from "./opddetails/iopddetails";
import { IPatientReport } from "../dialogs/patient-report-print/ipatient-report";

@Injectable({ providedIn: 'root' })
export class OPDService {

    private _OPD: BehaviorSubject<Iopd | null> = new BehaviorSubject<Iopd | null>(null);
    private _OPDclinical: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);
    private _patientReport: BehaviorSubject<IPatientReport | null> = new BehaviorSubject<IPatientReport | null>(null);

    constructor(
        private _httpClient: HttpClient
    ) { }


    get OPD$(): Observable<Iopd | null> {
        return this._OPD.asObservable();
    }

    get OPDRecord$(): Observable<any | null> {
        return this._OPDclinical.asObservable();
    }

    createOPD(opddata: any): Observable<BaseRespone<any>> {
        return this._httpClient.post<BaseRespone<any>>(`${environment.baseURl}/OPD`, opddata).pipe(
            tap((response) => {
                this._OPD.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to creating OPD:', error);
                throw error;
            })
        );
    }

    updateOPD(opddata: Iopd): Observable<BaseRespone<Iopd>> {
        return this._httpClient.put<BaseRespone<Iopd>>(`${environment.baseURl}/OPD`, opddata).pipe(
            tap((response) => {
                this._OPD.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to updating OPD:', error);
                throw error;
            })
        );
    }

    getOPDById(id: number): Observable<BaseRespone<any>> {
        return this._httpClient.get<BaseRespone<any>>(`${environment.baseURl}/OPD/${id}`).pipe(
            tap((response) => {
                this._OPD.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to fetching OPD by ID:', error);
                throw error;
            })
        );
    }

    deleteOPDClinicalRecord(id: number): Observable<BaseRespone<any>> {
        return this.OPDRecord$.pipe(
            take(1),
            switchMap(OPDRecord =>
                this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseURl}/OPDClinicalRecord/${id}`).pipe(
                    map((deletedResponse) => {
                        if (deletedResponse.result) {
                            const updatedOPDRecord = OPDRecord.filter((item: { id: number; }) => item.id !== id);
                            this._OPDclinical.next(updatedOPDRecord);
                        }
                        return deletedResponse;
                    })
                )
            ), catchError((error) => {
                return of(error.error);
            })
        );
    }

    createOPDClinicalRecord(OPDClinicalRecord: OPDRecord): Observable<BaseRespone<OPDRecord>> {
        return this._httpClient.post<BaseRespone<OPDRecord>>(`${environment.baseURl}/OPDClinicalRecord`, OPDClinicalRecord).pipe(
            tap((response) => {
                this._OPDclinical.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to creating OPDclinical:', error);
                throw error;
            })
        );
    }

    addDiagnosisFromMaster(request: AddDiagnosisFromMasterRequest): Observable<BaseRespone<DiagnosisFromMasterResult>> {
        return this._httpClient.post<BaseRespone<DiagnosisFromMasterResult>>(`${environment.baseURl}/OPDClinicalRecord/FromDiagnosisMaster`, request).pipe(
            catchError((error) => {
                return of(error.error);
            })
        );
    }

    addDiagnosesFromMaster(request: BulkAddDiagnosisFromMasterRequest): Observable<BaseRespone<BulkDiagnosisOutcomeResult[]>> {
        return this._httpClient.post<BaseRespone<BulkDiagnosisOutcomeResult[]>>(
            `${environment.baseURl}/OPDClinicalRecord/FromDiagnosisMaster/bulk`,
            request
        ).pipe(
            catchError((error) => of(error.error))
        );
    }

    updateOPDClinicalRecord(OPDClinicalRecord: any): Observable<BaseRespone<any>> {
        return this._httpClient.put<BaseRespone<any>>(`${environment.baseURl}/OPDClinicalRecord`, OPDClinicalRecord).pipe(
            tap((response) => {
                this._OPDclinical.next(response.result);
            }),
            catchError((error) => {
                return of(error.error);
            })
        );
    }

    getOPDClinical(id: number): Observable<BaseRespone<any>> {
        return this._httpClient.get<BaseRespone<any>>(`${environment.baseURl}/OPDClinicalRecord/${id}`).pipe(
            tap((response) => {
                this._OPDclinical.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to fetching OPD by ID:', error);
                throw error;
            })
        );
    }

    getPatientReport(id: number): Observable<BaseRespone<any>> {
        return this._httpClient.get<BaseRespone<any>>(`${environment.baseURl}/OPDClinicalRecord/report/${id}`).pipe(
            tap((response) => {
                this._patientReport.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to fetching by ID:', error);
                throw error;
            })
        );
    }

    createPatientToOPD(opddata: IpatientToOPD): Observable<BaseRespone<Iopd>> {
        return this._httpClient.post<BaseRespone<Iopd>>(`${environment.baseURl}/OPD/PatientToOPD`, opddata).pipe(
            tap((response) => {
                this._OPD.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to creating OPD:', error);
                throw error;
            })
        );
    }
}
